import * as React from 'react';
import { CTAButton } from '../../../controls/ButtonBase/index';
import { useLocalization } from '../../../context/Localization/localization';
import { Heading } from '../../../controls/Heading/Heading';
import { VersionInfo } from '../../../controls/VersionInfo/VersionInfo';
import { useQRScanner } from '../../../context/hardware';
import { useFullReset, useKioskImage, useKioskState } from '../../../context/Kiosk/kiosk';
import { ScrollableBody } from '../../Layout/ScrollableBody';
import { KioskImageDisplay } from '../../../controls/KioskImageDisplay';
import { default as css } from './MfaQr.module.scss';
import { useHideKioskBackground } from '../../../context/Page/page';
import { useAdminQrLogin } from '../../../context/Admin/admin';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { ScanErrorCode } from '../../../context/mzero';
import { MachineId } from './MachineId/MachineId';
import { GMDialog, GMDialogRetryCancel } from '../../../controls/Dialog';
import { GMButton } from '../../../controls/Button';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { useDevelopmentState } from '../../../context/Development/Development';
import { ScanQRCodeDevTools } from '../../SaveSession/ScanQRCode/ScanQRCodeDevTools/ScanQRCodeDevTools';
import { GMButtonGroup } from '../../../controls/ButtonGroup';

interface IProps {
    onProceed: () => void;
    onSkip: () => void;
}

export const MfaQr = (props: IProps) => {
    const Localization = useLocalization();
    const { key } = useKioskState();
    const { rawResult, inProgress, setResult, start, error: scanError, reset: scannerReset, simulateError } = useQRScanner(false);
    const [error, setError] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState(false);
    const [scannerUnavailableError, setScannerUnavailableError] = React.useState<boolean>(false);
    const lastRetryTime = React.useRef<number>(0);
    const scanQRCodeKioskImage = useKioskImage('ScanQRCode');
    const validate = useAdminQrLogin();
    const reset = useFullReset();
    const hideKioskBackground = useHideKioskBackground();
    const { isDev } = useDevelopmentState();
    const [isDevToolsOpen, setIsDevToolsOpen] = React.useState<boolean>(false);

    React.useEffect(() => {
        hideKioskBackground();
    }, [hideKioskBackground]);

    React.useEffect(() => {
        !rawResult && !scanError && start();
    }, [rawResult, scanError, start]);

    React.useEffect(() => {
        if (!scanError)
            return undefined;
        const elapsed = Date.now() - lastRetryTime.current;
        if (elapsed < 2000) {
            const timer = setTimeout(() => setScannerUnavailableError(true), 2000 - elapsed);
            return () => clearTimeout(timer);
        }
        setScannerUnavailableError(true);
        return undefined;
    }, [scanError]);

    React.useEffect(() => {
        async function fetchData() {
            const result = await validate({
                kioskKey: key,
                qrCode: rawResult ?? '',
            });

            if (result.error) {
                setError(true);
                if (setResult) {
                    setResult('');
                }
            }
            else {
                props.onProceed();
            }

            setLoading(false);
        }

        if (rawResult?.length && !loading && !error) {
            setLoading(true);
            fetchData();
        }
    }, [rawResult, loading, error, validate, key, props, setResult]);

    return (
        <>
            <div className={css.machineInfo}>
                <VersionInfo />
                <MachineId />
            </div>
            <ScrollableBody
                heading={
                    <Heading
                        primaryHeading={Localization.AdminLogin}
                        secondaryHeading={
                            <div style={{
                                textAlign: 'left',
                                fontSize: '32px',
                            }}
                            >
                                <div style={{ marginBottom: '10px' }}>{Localization.QRCodeExplaination1}</div>
                                <div>{Localization.QRCodeExplaination2}</div>
                            </div>
                        }
                        roundedIcon={
                            <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                                <GMSizedIcon iconType={'Key'} iconSize={'large'} />
                            </GMRoundedIcon>
                        }
                    />
                }
                proceedButtons={
                    isDev ? (
                        <GMButtonGroup
                            buttons={[
                                <CTAButton
                                    styleType={'primary'}
                                    onClick={() => {
                                        if (!isDevToolsOpen) {
                                            setIsDevToolsOpen(true);
                                        }
                                    }}
                                    label={'Open Dev Tools'}
                                    id={'Open Dev Tools'}
                                    ariaLabel={'Click to open Dev Tools dialog'}
                                    kioskOptions={{
                                        inlineOptions: {
                                            sizedIcon: (
                                                <GMSizedIcon
                                                    iconType={'Menu'}
                                                    iconSize={'small'}
                                                    ariaLabel={'Menu icon'}
                                                />
                                            ),
                                        },
                                    }}
                                />,
                                ...(simulateError ? [
                                    <CTAButton
                                        styleType={'secondary'}
                                        onClick={() => simulateError(ScanErrorCode.unknown)}
                                        label={'Simulate Scanner Error'}
                                        id={'Simulate Scanner Error'}
                                        ariaLabel={'Simulate Scanner Error'}
                                        hideIcon
                                    />,
                                ] : []),
                            ]}
                        />
                    ) : undefined}
                hideSpacer={!isDev}
            >
                {inProgress && <Spinner />}
                <KioskImageDisplay
                    left={{
                        className: css.left,
                        image: scanQRCodeKioskImage,
                    }}
                    right={{
                        top: {
                            text: Localization.ScanQRCodeUsingTheHighlightedReader,
                        },
                        image: useKioskImage('PhoneQR'),
                        bottom: Localization.PositionTheQRCode,
                    }}
                />

                <div className={css.top}>{Localization.DontHaveAQRCode}</div>
                <div className={css.skipButton}>
                    <CTAButton
                        onClick={props.onSkip}
                        styleType={'primary'}
                        type={'submit'}
                        label={Localization.EnterPin}
                        id={Localization.EnterPin}
                        ariaLabel={Localization.EnterPin}
                    />
                </div>

                {isDev && setResult && (
                    <ScanQRCodeDevTools
                        onScan={({ mfaCode }) => {
                            setResult(mfaCode ?? '');
                        }}
                        isDevToolsOpen={isDevToolsOpen}
                        setIsDevToolsOpen={setIsDevToolsOpen}
                        qrCodeType={'mfa'}
                    />
                )}
                <GMDialogRetryCancel
                    ariaLabel={Localization.InvalidQRCode}
                    title={Localization.InvalidQRCode}
                    open={!!error && !loading}
                    onCancel={() => {
                        reset();
                    }}
                    onRetry={() => {
                        scannerReset();
                        start();
                        setError(false);
                    }}
                    className={css.dialogTitle}
                >
                    {Localization.PleaseTryAgain}
                </GMDialogRetryCancel>
                <GMDialog
                    ariaLabel={Localization.ScannerUnavailable}
                    title={Localization.ScannerUnavailable}
                    open={!!scannerUnavailableError}
                    actions={[
                        <GMButton
                            key={'retry'}
                            styleType={'secondary'}
                            onClick={() => {
                                lastRetryTime.current = Date.now();
                                scannerReset();
                                start();
                                setScannerUnavailableError(false);
                            }}
                            id={Localization.Retry}
                            title={Localization.Retry}
                            ariaLabel={Localization.Retry}
                        >
                            {Localization.Retry}
                        </GMButton>,
                        <GMButton
                            key={'enter-pin'}
                            styleType={'primary'}
                            onClick={props.onSkip}
                            id={Localization.EnterPin}
                            title={Localization.EnterPin}
                            ariaLabel={Localization.EnterPin}
                        >
                            {Localization.EnterPin}
                        </GMButton>,
                    ]}
                    className={css.dialogTitle}
                >
                    <div>{Localization.ScannerUnavailableBody}</div>
                    <br />
                    <div style={{ whiteSpace: 'pre-line' }}>{Localization.ScannerUnavailableContactDetails}</div>
                </GMDialog>
            </ScrollableBody>
        </>
    );
};
